import { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  Phone, Mail, MapPin, Clock, Send, MessageSquare, 
  Globe, CheckCircle2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import Turnstile from '../components/Turnstile';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
    service_interest: ''
  });

  const serviceOptions = [
    { value: 'air', labelKey: 'nav.airFreight' },
    { value: 'ocean', labelKey: 'nav.oceanFreight' },
    { value: 'road', labelKey: 'nav.roadFreight' },
    { value: 'multimodal', labelKey: 'quote.transportModes.multimodal' },
    { value: 'customs', labelKey: 'nav.customs' },
    { value: 'warehousing', labelKey: 'nav.warehousing' },
    { value: 'other', labelKey: 'contact.form.otherInquiry' }
  ];

  const contactInfo = [
    {
      id: 'phone',
      icon: Phone,
      titleKey: 'contact.info.phone',
      content: '+31 62 538 1824',
      subtitleKey: 'contact.info.phoneHours'
    },
    {
      id: 'email',
      icon: Mail,
      titleKey: 'contact.info.email',
      content: 'info@elementarycargo.com',
      subtitleKey: 'contact.info.emailResponse'
    },
    {
      id: 'headquarters',
      icon: MapPin,
      titleKey: 'contact.info.headquarters',
      content: 'The Netherlands',
      subtitleKey: 'contact.info.headquartersLocation'
    },
    {
      id: 'hours',
      icon: Clock,
      titleKey: 'contact.info.businessHours',
      contentKey: 'contact.info.businessDays',
      subtitleKey: 'contact.info.businessTime'
    }
  ];

  const handleChange = (field, value) => {
    // Phone validation - only allow digits and + symbol
    if (field === 'phone') {
      value = value.replace(/[^0-9+\-\s]/g, '');
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    // Name validation: letters, spaces, hyphens, apostrophes only
    const nameRegex = /^[A-Za-zÀ-ÿ\s\-']{2,50}$/;
    if (!nameRegex.test(formData.name)) {
      toast.error('Name must be 2-50 characters and contain only letters, spaces, hyphens, and apostrophes');
      return false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }

    // Phone validation (optional but must be valid if provided)
    if (formData.phone) {
      const phoneClean = formData.phone.replace(/[\s\-]/g, '');
      const phoneRegex = /^\+?[0-9]{7,15}$/;
      if (!phoneRegex.test(phoneClean)) {
        toast.error('Phone number must be 7-15 digits, optionally starting with +');
        return false;
      }
    }

    // Subject validation
    if (formData.subject.length < 2 || formData.subject.length > 200) {
      toast.error('Subject must be between 2 and 200 characters');
      return false;
    }

    // Message validation
    if (formData.message.length < 10 || formData.message.length > 2000) {
      toast.error('Message must be between 10 and 2000 characters');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error(t('contact.form.fillRequired'));
      return;
    }

    // Run frontend validation
    if (!validateForm()) {
      return;
    }

    // Check Turnstile verification
    if (!turnstileToken) {
      toast.error('Please complete the verification challenge');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        turnstile_token: turnstileToken
      });
      setSubmitted(true);
      toast.success(t('contact.success.title'));
    } catch (error) {
      console.error('Failed to send message:', error);
      // Show specific backend validation error if available
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          toast.error(detail[0]?.msg || t('contact.form.sendError'));
        } else {
          toast.error(detail);
        }
      } else {
        toast.error(t('contact.form.sendError'));
      }
      // Reset turnstile on error
      setTurnstileToken(null);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen pt-32 pb-20 bg-steel-50" data-testid="contact-success">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-lg mx-auto text-center"
          >
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="font-outfit text-3xl font-bold text-navy-800 mb-4">
              {t('contact.success.title')}
            </h1>
            <p className="text-steel-600 mb-8">
              {t('contact.success.message')}
            </p>
            <Button onClick={() => window.location.href = '/'} className="btn-primary" data-testid="back-home-btn">
              {t('common.backHome')}
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="contact-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-navy-800">
        <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-10" />
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="text-gold text-sm font-bold tracking-widest uppercase mb-4 block">{t('contact.title')}</span>
            <h1 className="font-outfit text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {t('contact.heading')}
            </h1>
            <p className="text-steel-300 text-xl leading-relaxed">
              {t('contact.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-steel-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-outfit text-2xl font-bold text-navy-800 mb-8">
                {t('contact.getInTouch')}
              </h2>
              
              <div className="space-y-6">
                {contactInfo.map((item) => (
                  <div key={item.id} className="flex items-start gap-4" data-testid={`contact-info-${item.id}`}>
                    <div className="w-12 h-12 bg-gold/10 flex items-center justify-center rounded-sm flex-shrink-0">
                      <item.icon className="w-5 h-5 text-gold" />
                    </div>
                    <div>
                      <h3 className="font-outfit font-bold text-navy-800">{t(item.titleKey)}</h3>
                      <p className="text-navy-800">{item.contentKey ? t(item.contentKey) : item.content}</p>
                      <p className="text-steel-500 text-sm">{t(item.subtitleKey)}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Global Presence */}
              <div className="mt-10 p-6 bg-navy-800 rounded-sm">
                <Globe className="w-8 h-8 text-gold mb-4" />
                <h3 className="font-outfit font-bold text-white mb-2">{t('contact.globalPresence.title')}</h3>
                <p className="text-steel-300 text-sm">
                  {t('contact.globalPresence.description')}
                </p>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2"
            >
              <Card className="shadow-xl border-0">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-8">
                    <MessageSquare className="w-6 h-6 text-gold" />
                    <h2 className="font-outfit text-2xl font-bold text-navy-800">{t('contact.sendMessage')}</h2>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <Label>{t('contact.form.fullName')} *</Label>
                        <Input
                          value={formData.name}
                          onChange={(e) => handleChange('name', e.target.value)}
                          placeholder={t('contact.form.fullName')}
                          data-testid="contact-name"
                        />
                      </div>
                      <div>
                        <Label>{t('contact.form.email')} *</Label>
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleChange('email', e.target.value)}
                          placeholder={t('login.emailPlaceholder')}
                          data-testid="contact-email"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <Label>{t('contact.form.phone')}</Label>
                        <Input
                          value={formData.phone}
                          onChange={(e) => handleChange('phone', e.target.value)}
                          placeholder="+1 (234) 567-890"
                          data-testid="contact-phone"
                        />
                      </div>
                      <div>
                        <Label>{t('contact.form.company')}</Label>
                        <Input
                          value={formData.company}
                          onChange={(e) => handleChange('company', e.target.value)}
                          placeholder={t('register.companyPlaceholder')}
                          data-testid="contact-company"
                        />
                      </div>
                    </div>

                    <div>
                      <Label>{t('contact.form.serviceInterest')}</Label>
                      <Select value={formData.service_interest} onValueChange={(v) => handleChange('service_interest', v)}>
                        <SelectTrigger data-testid="service-interest-select">
                          <SelectValue placeholder={t('contact.form.selectService')} />
                        </SelectTrigger>
                        <SelectContent>
                          {serviceOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {t(option.labelKey)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>{t('contact.form.subject')} *</Label>
                      <Input
                        value={formData.subject}
                        onChange={(e) => handleChange('subject', e.target.value)}
                        placeholder={t('contact.form.subjectPlaceholder')}
                        data-testid="contact-subject"
                      />
                    </div>

                    <div>
                      <Label>{t('contact.form.message')} *</Label>
                      <Textarea
                        value={formData.message}
                        onChange={(e) => handleChange('message', e.target.value)}
                        placeholder={t('contact.form.messagePlaceholder')}
                        rows={5}
                        data-testid="contact-message"
                      />
                    </div>

                    {/* Cloudflare Turnstile CAPTCHA */}
                    <div className="flex justify-start">
                      <Turnstile
                        onVerify={(token) => setTurnstileToken(token)}
                        onError={() => {
                          setTurnstileToken(null);
                          toast.error('Verification failed. Please try again.');
                        }}
                        onExpire={() => setTurnstileToken(null)}
                        theme="light"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      className="btn-accent w-full sm:w-auto" 
                      disabled={loading || !turnstileToken}
                      data-testid="contact-submit-btn"
                    >
                      {loading ? t('contact.form.sending') : t('contact.form.submit')}
                      <Send className="ml-2 w-4 h-4" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
